import { useMutation } from '@tanstack/react-query';
import { addBagToShipment, deleteBagFromShipment } from '../api';
import { AddBagToShipmentStatus, RemoveBagFromShipmentStatus } from './shipment';
import { SoundNotificationContext, useNotificationsV2 } from '@uni-space/uni-components';
import { useContext } from 'react';
import { useShipmentStore } from './useShipmentStore';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { ErrorDTO } from '@shared/types/api';

export default function useSHipmentScanningFetch() {
  const { createNotification } = useNotificationsV2();
  const { onSoundSuccess, onSoundError } = useContext(SoundNotificationContext);
  const { currentShipment, handleChangeBagData, setHasSuccessfulPick } = useShipmentStore();
  const { t } = useTranslation();

  const { mutateAsync: onAddBag, isPending: isAdding } = useMutation({
    mutationKey: ['addBagToShipment', currentShipment?.id],
    mutationFn: addBagToShipment,

    onSuccess: (resp, variables) => {
      const bagNumber = variables.bagNumber;

      switch (resp.status) {
        case AddBagToShipmentStatus.Added: {
          createNotification({
            type: 'success',
            message: {
              title: t('shipmentScanningBag.add.success.title'),
              desc: t('shipmentScanningBag.add.success.desc', { bagNumber }),
            },
          });
          handleChangeBagData(resp.bags.reverse());
          setHasSuccessfulPick(true);
          onSoundSuccess();
          return;
        }

        case AddBagToShipmentStatus.NotFound: {
          createNotification({
            type: 'error',
            message: {
              title: t('shipmentScanningBag.add.notFound.title'),
              desc: t('shipmentScanningBag.add.notFound.desc', { bagNumber }),
            },
          });
          onSoundError();
          return;
        }

        case AddBagToShipmentStatus.ShipmentNotFound: {
          createNotification({
            type: 'error',
            message: {
              title: t('shipmentScanningBag.add.shipmentNotFound.title'),
              desc: t('shipmentScanningBag.add.shipmentNotFound.desc'),
            },
          });
          onSoundError();
          return;
        }

        case AddBagToShipmentStatus.AlreadyAddedToAnotherShipment: {
          createNotification({
            type: 'error',
            message: {
              title: t('shipmentScanningBag.add.alreadyInAnother.title'),
              desc: t('shipmentScanningBag.add.alreadyInAnother.desc', { bagNumber }),
            },
          });
          onSoundError();
          return;
        }

        case AddBagToShipmentStatus.PostalBagWasNotLabeled: {
          createNotification({
            type: 'error',
            message: {
              title: t('shipmentScanningBag.add.postalBagWasNotLabeled.title'),
              desc: t('shipmentScanningBag.add.postalBagWasNotLabeled.desc', { bagNumber }),
            },
          });
          onSoundError();
          return;
        }

        case AddBagToShipmentStatus.ShipmentCompleted: {
          createNotification({
            type: 'error',
            message: {
              title: t('shipmentScanningBag.add.shipmentCompleted.title'),
              desc: t('shipmentScanningBag.add.shipmentCompleted.desc'),
            },
          });
          onSoundError();
          return;
        }

        case AddBagToShipmentStatus.ReturnBagRequiresReturnShipment: {
          createNotification({
            type: 'error',
            message: {
              title: t('shipmentScanningBag.add.returnBagRequiresReturnShipment.title'),
              desc: t('shipmentScanningBag.add.returnBagRequiresReturnShipment.desc', { bagNumber }),
            },
          });
          onSoundError();
          return;
        }

        case AddBagToShipmentStatus.ReturnShipmentRequiresReturnBag: {
          createNotification({
            type: 'error',
            message: {
              title: t('shipmentScanningBag.add.returnShipmentRequiresReturnBag.title'),
              desc: t('shipmentScanningBag.add.returnShipmentRequiresReturnBag.desc', { bagNumber }),
            },
          });
          onSoundError();
          return;
        }

        case AddBagToShipmentStatus.InternalError:
        default: {
          createNotification({
            type: 'error',
            message: {
              title: t('common.serverError.title'),
              desc: t('common.serverError.desc'),
            },
          });
          onSoundError();
          return;
        }
      }
    },

    onError: (e: AxiosError<ErrorDTO>) => {
      createNotification({
        type: 'error',
        message: {
          title: t('common.systemError.title'),
          desc: e.response?.data.message ?? t('common.systemError.desc'),
        },
      });
      onSoundError();
    },
  });
  const { mutateAsync: onDeleteBag, isPending: isDeleting } = useMutation({
    mutationKey: ['deleteBagFromShipment', currentShipment?.id],
    mutationFn: deleteBagFromShipment,

    onSuccess: (resp, variables) => {
      const bagNumber = variables.bagNumber;

      switch (resp.status) {
        case RemoveBagFromShipmentStatus.Removed: {
          createNotification({
            type: 'success',
            message: {
              title: t('shipmentScanningBag.remove.success.title'),
              desc: t('shipmentScanningBag.remove.success.desc', { bagNumber }),
            },
          });
          if (resp.bags) handleChangeBagData(resp.bags);
          setHasSuccessfulPick(true);
          onSoundSuccess();
          return;
        }

        case RemoveBagFromShipmentStatus.BagNotFound: {
          createNotification({
            type: 'error',
            message: {
              title: t('shipmentScanningBag.remove.bagNotFound.title'),
              desc: t('shipmentScanningBag.remove.bagNotFound.desc', { bagNumber }),
            },
          });
          onSoundError();
          return;
        }

        case RemoveBagFromShipmentStatus.ShipmentNotFound: {
          createNotification({
            type: 'error',
            message: {
              title: t('shipmentScanningBag.remove.shipmentNotFound.title'),
              desc: t('shipmentScanningBag.remove.shipmentNotFound.desc'),
            },
          });
          onSoundError();
          return;
        }

        case RemoveBagFromShipmentStatus.ShipmentAlreadyCompleted: {
          createNotification({
            type: 'error',
            message: {
              title: t('shipmentScanningBag.remove.shipmentCompleted.title'),
              desc: t('shipmentScanningBag.remove.shipmentCompleted.desc'),
            },
          });
          onSoundError();
          return;
        }

        default: {
          createNotification({
            type: 'error',
            message: {
              title: t('common.serverError.title'),
              desc: t('common.serverError.desc'),
            },
          });
          onSoundError();
          return;
        }
      }
    },

    onError: () => {
      createNotification({
        type: 'error',
        message: {
          title: t('common.systemError.title'),
          desc: t('common.systemError.desc'),
        },
      });
      onSoundError();
    },
  });

  return { onAddBag, isAdding, onDeleteBag, isDeleting };
}
